import { Link } from "react-router";
import { ArrowLeft, Target, CheckCircle, Circle, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Main } from "@/components/shared/Main";
import { Header } from "@/components/shared/Header";

export function MaskWorkflowDocPage() {
  return (
    <>
      <Header />
      <Main>
        <div className="mb-8">
          <Button asChild variant="ghost" className="mb-4">
            <Link to="/docs" className="flex items-center">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Documentation
            </Link>
          </Button>

          <div className="flex items-center mb-4">
            <div className="w-12 h-12 rounded-xl bg-linear-to-br from-blue-500 to-purple-600 flex items-center justify-center mr-4">
              <Target className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900">Mask Workflow</h1>
              <p className="text-xl text-gray-600">
                Complete guide to preparing masks for measurement
              </p>
            </div>
          </div>
        </div>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 mb-4">
              The mask workflow is a sequential process that guides you through preparing a{" "}
              <a href="/docs/glossary#mask" className="text-blue-600 hover:underline">
                mask
              </a>{" "}
              for measurement requests. Each step must be completed before moving to the next,
              ensuring all required data is collected before measurement can begin.
            </p>

            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 mb-4">
              <h4 className="font-semibold text-yellow-900 mb-2">
                How to Access the Mask Workflow:
              </h4>
              <ol className="text-yellow-800 text-sm space-y-1">
                <li>
                  1. Navigate to the{" "}
                  <Link to="/projects" className="underline font-medium">
                    Projects page
                  </Link>
                </li>
                <li>2. Select a project to open its details page</li>
                <li>3. Select a mask from the "Mask Selection" dropdown</li>
                <li>4. The mask workflow steps will appear below</li>
              </ol>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-900 mb-2">Prerequisites:</h4>
              <ul className="text-blue-800 text-sm space-y-1">
                <li>• An existing project with at least one mask</li>
                <li>• Access to mask design XML files in the new format</li>
                <li>• Chip positioning files (ordered or unordered format)</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-8">
          {/* Step 1: Create Mask */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-100">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <CardTitle>Step 1: Create Mask</CardTitle>
                  <p className="text-sm text-gray-600">Automatic - Basic mask information</p>
                </div>
                <Badge className="ml-auto bg-green-100 text-green-800">Automatic</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 mb-4">
                This step is automatically completed when you create a mask in your project. The
                mask contains basic information like name and PCM version.
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">What's included:</h4>
                <ul className="text-gray-700 text-sm space-y-1">
                  <li>• Mask name and description</li>
                  <li>• Associated PCM version</li>
                  <li>• Project relationship</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Step 2: Design XML */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100">
                  <Circle className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <CardTitle>Step 2: Design XML</CardTitle>
                  <p className="text-sm text-gray-600">Testing Team - Link design XML files</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 mb-4">
                Upload a ZIP file containing the mask design XML files. This step links the
                structural design information to your mask.
              </p>

              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-blue-900 mb-2">How to upload:</h4>
                  <ol className="text-blue-800 text-sm space-y-1">
                    <li>1. Navigate to your project and select the mask</li>
                    <li>2. Click on "Step 2: Design XML" in the workflow</li>
                    <li>3. Click the "Click here to select a file" button</li>
                    <li>4. Select your ZIP file containing the design XML</li>
                  </ol>
                </div>

                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 shrink-0" />
                    <div>
                      <h4 className="font-semibold text-yellow-900 mb-2">Important Notes:</h4>
                      <ul className="text-yellow-800 text-sm space-y-1">
                        <li>• Design XML must be in the new format</li>
                        <li>• Only one design XML file can be uploaded per mask</li>
                        <li>• There is no "remove" button - be careful with uploads</li>
                        <li>• If upload fails, check file formatting</li>
                        <li>• Contact an admin to fix mistakes in the database</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Step 3: Project Specs List */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100">
                  <Circle className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <CardTitle>Step 3: Project Specs List</CardTitle>
                  <p className="text-sm text-gray-600">Project Manager - Create specifications</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 mb-4">
                Create at least one specifications list for this mask. Specs lists define the
                testing parameters and requirements.
              </p>

              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-2">Next Steps:</h4>
                <p className="text-blue-800 text-sm">
                  For detailed instructions on creating project specifications, see the{" "}
                  <Link to="/docs/create-project-spec" className="underline font-medium">
                    Create Project Spec for Mask
                  </Link>{" "}
                  guide.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Step 4: Chip Position Files */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100">
                  <Circle className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <CardTitle>Step 4: Chip Position Files</CardTitle>
                  <p className="text-sm text-gray-600">Project Manager - Upload positioning data</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 mb-4">
                Upload chip positioning files that define the physical layout and coordinates of
                chips on the mask.
              </p>

              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-blue-900 mb-2">How to upload:</h4>
                  <ol className="text-blue-800 text-sm space-y-1">
                    <li>1. Click on "Step 4: Chip Position Files" in the workflow</li>
                    <li>2. Click the "Click here to select a file" button</li>
                    <li>3. Select your chip position file(s)</li>
                    <li>4. You can upload multiple files if needed</li>
                  </ol>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Supported File Types:</h4>
                  <ul className="text-gray-700 text-sm space-y-1">
                    <li>
                      • <strong>Ordered format:</strong> Files with + symbols and numbers
                    </li>
                    <li>
                      • <strong>Unordered format:</strong> Files with X symbols
                    </li>
                  </ul>
                </div>

                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 shrink-0" />
                    <div>
                      <h4 className="font-semibold text-yellow-900 mb-2">Important:</h4>
                      <p className="text-yellow-800 text-sm">
                        There is no remove option for uploaded files. Be careful when uploading and
                        contact an admin if you need to remove or replace files.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Step 5: Test Labels */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100">
                  <Circle className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <CardTitle>Step 5: Test Labels</CardTitle>
                  <p className="text-sm text-gray-600">
                    Project Manager - Create and validate test labels
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 mb-4">
                Create one or more test labels for the mask and validate them through a test flight
                measurement request.
              </p>

              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-blue-900 mb-2">Process Overview:</h4>
                  <ol className="text-blue-800 text-sm space-y-1">
                    <li>1. Create test labels for your mask</li>
                    <li>2. Run a test flight to validate the test labels</li>
                    <li>3. Step completes when all test labels are validated</li>
                  </ol>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Test Flight Details:</h4>
                  <ul className="text-gray-700 text-sm space-y-1">
                    <li>• Test flight is a special measurement request</li>
                    <li>
                      • Fixed naming format:{" "}
                      <code className="bg-white px-1 rounded">[testflight] + mask name</code>
                    </li>
                    <li>• Uses the specific test label being validated</li>
                    <li>• Required to turn this step green</li>
                  </ul>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-blue-900 mb-2">Detailed Instructions:</h4>
                  <p className="text-blue-800 text-sm">
                    For comprehensive guidance on creating test labels, see the{" "}
                    <Link to="/docs/create-test-label" className="underline font-medium">
                      Create New Test Label in Mask
                    </Link>{" "}
                    documentation.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Step 6: Measurement Requests */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100">
                  <Circle className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <CardTitle>Step 6: Measurement Requests</CardTitle>
                  <p className="text-sm text-gray-600">
                    Project Manager - Plan and execute measurements
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 mb-4">
                View and manage all measurement requests associated with this mask. This step shows
                the final outcome of your mask preparation workflow.
              </p>

              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">What you'll see:</h4>
                  <ul className="text-gray-700 text-sm space-y-1">
                    <li>• List of all measurement requests for this mask</li>
                    <li>• Status of each request (Planned, Executed, Analysed)</li>
                    <li>• Links to detailed measurement request pages</li>
                    <li>• Option to create new measurement requests</li>
                  </ul>
                </div>

                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 shrink-0" />
                    <div>
                      <h4 className="font-semibold text-yellow-900 mb-2">
                        Planning Without Validation:
                      </h4>
                      <p className="text-yellow-800 text-sm">
                        While the system allows planning measurement requests even if test labels
                        are not validated, this is not recommended. It's best practice to complete
                        test flight validation before proceeding with production measurements.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-blue-900 mb-2">Related Documentation:</h4>
                  <ul className="text-blue-800 text-sm space-y-1">
                    <li>
                      •{" "}
                      <Link to="/docs/create-measurement-request" className="underline font-medium">
                        Create Measurement Request
                      </Link>
                    </li>
                    <li>
                      •{" "}
                      <Link to="/docs/plan-measurement-requests" className="underline font-medium">
                        Plan Measurement Requests
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Summary Section */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle>Workflow Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 mb-4">
              The mask workflow ensures that all necessary data and configurations are in place
              before measurement requests can be executed effectively. Each step builds upon the
              previous one, creating a comprehensive foundation for accurate and reliable
              measurements.
            </p>

            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-semibold text-green-900 mb-2">Completion Checklist:</h4>
              <ul className="text-green-800 text-sm space-y-1">
                <li>✓ Mask created with basic information</li>
                <li>✓ Design XML uploaded and linked</li>
                <li>✓ Project specifications defined</li>
                <li>✓ Chip position files uploaded</li>
                <li>✓ Test labels created and validated through test flight</li>
                <li>✓ Ready for production measurement requests</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </Main>
    </>
  );
}
